import { cn } from '../../../lib/utils';

/** Expanded sidebar width */
export const SIDEBAR_EXPANDED_WIDTH = '17.5rem';

/** Flyout panel width when rail is collapsed */
export const SIDEBAR_FLYOUT_WIDTH_PX = 300;

export const sidebarShellClass = (collapsed: boolean) =>
  cn(
    'border-r border-white/[0.08]',
    'bg-gradient-to-b from-[#12161d] via-[#0f1319] to-[#0c1016]',
    'dark:from-[#0a0e14] dark:via-[#080b10] dark:to-[#06080c]',
    collapsed && 'shadow-[inset_-1px_0_0_0_rgba(255,255,255,0.04)]',
  );

export const sidebarHeaderClass = (collapsed: boolean) =>
  cn(
    'flex flex-shrink-0 items-center border-b border-white/[0.08]',
    collapsed ? 'flex-col justify-center gap-2 px-2 py-3 min-h-[4.5rem]' : 'justify-between px-4 h-16',
  );

export const sidebarNavScrollClass = (collapsed: boolean) =>
  cn(
    'flex-1 min-h-0 min-w-0',
    collapsed ? 'rail-nav-scroll overflow-y-auto px-2 py-3' : 'overflow-y-auto px-3 py-4',
  );

export const sidebarNavGroupClass = () => 'space-y-1 min-w-0';

export const sidebarNavItemClass = (active: boolean) =>
  cn(
    'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200',
    active
      ? 'bg-indigo-500/20 text-white shadow-sm ring-1 ring-inset ring-indigo-400/25'
      : 'text-slate-300 hover:bg-white/[0.07] hover:text-white',
  );

export const sidebarNavChildClass = (active: boolean) =>
  cn(
    'flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-all duration-200',
    active
      ? 'bg-white/[0.1] text-white font-medium'
      : 'text-slate-400 hover:bg-white/[0.06] hover:text-slate-100',
  );

export const sidebarIconWellClass = (active: boolean) =>
  cn(
    'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors',
    active ? 'bg-indigo-500/30 text-indigo-100' : 'bg-white/[0.06] text-slate-300 group-hover:bg-white/10 group-hover:text-white',
  );

export const sidebarRailItemClass = (active: boolean) =>
  cn(
    'relative flex h-10 w-10 max-w-full items-center justify-center rounded-xl transition-all duration-200',
    active
      ? 'bg-indigo-500/25 text-white shadow-lg shadow-indigo-950/40 ring-1 ring-indigo-400/35'
      : 'text-slate-400 hover:bg-white/[0.08] hover:text-slate-100',
    active &&
      'before:absolute before:left-[-0.4rem] before:top-1/2 before:h-5 before:w-[3px] before:-translate-y-1/2 before:rounded-full before:bg-indigo-400',
  );

export const sidebarRailWrapClass = () => 'relative flex w-full max-w-full justify-center py-0.5';

export const sidebarCollapseToggleClass = () =>
  cn(
    'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/10',
    'bg-white/[0.04] text-slate-300 transition-all hover:bg-white/10 hover:text-white',
  );

export const sidebarFooterClass = (collapsed: boolean) =>
  cn('mt-auto flex-shrink-0 border-t border-white/[0.08]', collapsed ? 'px-2 py-3' : 'px-3 pb-3 pt-2');

export const sidebarUserButtonClass = (collapsed: boolean) =>
  cn(
    'w-full transition-colors rounded-xl',
    collapsed
      ? 'flex justify-center p-1'
      : 'flex items-center gap-3 px-2.5 py-2.5 text-left hover:bg-white/[0.07]',
  );

export const sidebarFlyoutPanelClass = () =>
  cn(
    'flex flex-col overflow-hidden rounded-2xl border border-white/10',
    'bg-[#151a22]/95 text-slate-100 shadow-2xl shadow-black/50 backdrop-blur-xl',
    'dark:bg-[#11161e]/98',
  );

export const sidebarFlyoutHeaderClass = () =>
  'flex shrink-0 items-center justify-between gap-2 border-b border-white/10 px-4 py-3';

export const sidebarFlyoutBodyClass = () => 'flex-1 min-h-0 overflow-y-auto px-2 py-2';

export const sidebarFlyoutItemClass = (active: boolean, nested?: boolean) =>
  cn(
    'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition-colors',
    nested ? 'justify-between' : '',
    active
      ? 'bg-indigo-500/20 text-white ring-1 ring-inset ring-indigo-400/20'
      : 'text-slate-300 hover:bg-white/[0.07] hover:text-white',
  );

export const sidebarViewsMenuClass = () =>
  cn(
    'min-w-[12.5rem] overflow-hidden rounded-xl border border-white/10 py-1.5',
    'bg-[#151a22]/98 text-slate-100 shadow-2xl shadow-black/50 backdrop-blur-xl',
  );

export const sidebarViewsMenuItemClass = (active: boolean) =>
  cn(
    'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors',
    active ? 'bg-indigo-500/20 text-white' : 'text-slate-300 hover:bg-white/[0.07] hover:text-white',
  );

export const sidebarSectionLabelClass = () =>
  'px-3 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500';
